// Page-visibility config for the SPA nav + route guards.
//
// The effective page config is folded into the public /api/config response
// by the backend (handlers/admin_pages.rs::effective_pages). This hook reads
// it so the nav can hide disabled/role-scoped pages and a route guard can
// redirect direct-URL access. Enforcement here is UX only — every page's API
// endpoints keep their own RBAC, so this is convenience + scoping, not the
// security boundary.

import { useQuery } from '@tanstack/react-query';
import type { EffectivePage, Role } from './useAdmin';

interface ConfigDto {
  pages?: EffectivePage[];
  branding?: Branding;
  modules?: PackageConfig[];
  /** The packages this site's plan includes (entitlement row; every module
   *  when the stamp has no row). Presentation only — the API gates by route. */
  entitled_modules?: ModuleKey[];
  dev_banner?: string;
  session_idle_minutes?: number;
  qros_cli?: QrosCliDownloads;
  issues_enabled?: boolean;
  anonymous_read?: boolean;
  property_graph_import?: boolean;
  marketing_panes?: MarketingPanes;
  max_load_bytes?: number;
  /** The site's query time budget in seconds, so a running query can say
   *  how much of it has gone. */
  query_timeout_secs?: number;
}

/** Who sees the Welcome page's capability panes on this instance
 *  (`TRES_API_MARKETING_PANES`). Operator-set per instance: a customer's site
 *  runs `off`, the preview runs `signed-in` while the copy is under review and
 *  `everyone` once it is public. */
export type MarketingPanes = 'off' | 'signed-in' | 'everyone';

/** Per-platform download URLs for the qros CLI, surfaced in the ? menu. Present
 *  only when the instance sets TRES_API_QROS_CLI_BASE_URL. */
export interface QrosCliDownloads {
  macos_arm64: string;
  windows_x64: string;
}

// The entitlement packages (handlers/modules.rs): each module groups the
// pages it owns. Badging is presentation only — enforcement is server-side
// against the same catalog (by API route, independent of page display).
export type ModuleKey = 'base' | 'data-viz' | 'llm' | 'agent' | 'doc-extract' | 'automation';

export interface PackageConfig {
  id: ModuleKey;
  label: string;
  pages: { key: string; beta: boolean }[];
}

/** One page's module assignment, flattened from the package list for badge
 *  lookups. */
export interface PageModule {
  key: string;
  module: ModuleKey;
  module_label: string;
  beta: boolean;
}

/** The Welcome page's per-instance branding (handlers/admin_branding.rs),
 *  set by an Admin. Both absent = the product default. `logo_updated_at`
 *  versions the public logo URL so a replacement is never served stale. */
export interface Branding {
  welcome_title: string | null;
  welcome_tagline: string | null;
  show_tagline: boolean;
  logo_updated_at: string | null;
}

export function logoUrl(branding: Branding | undefined): string | null {
  if (!branding?.logo_updated_at) return null;
  return `/api/branding/logo?v=${encodeURIComponent(branding.logo_updated_at)}`;
}

export function useBranding() {
  return useQuery<Branding>({
    queryKey: ['branding'],
    queryFn: async () => {
      const res = await fetch('/api/config');
      if (!res.ok) throw new Error(`/api/config: HTTP ${res.status}`);
      const data = (await res.json()) as ConfigDto;
      return (
        data.branding ?? {
          welcome_title: null,
          welcome_tagline: null,
          show_tagline: true,
          logo_updated_at: null,
        }
      );
    },
    staleTime: 60_000,
  });
}

/** Optional site-wide banner (e.g. preview "under active development").
 *  Returns null when unset. */
export function useDevBanner() {
  return useQuery<string | null>({
    queryKey: ['dev-banner'],
    queryFn: async () => {
      const res = await fetch('/api/config');
      if (!res.ok) throw new Error(`/api/config: HTTP ${res.status}`);
      const data = (await res.json()) as ConfigDto;
      return data.dev_banner ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function usePagesConfig() {
  return useQuery<EffectivePage[]>({
    queryKey: ['pages-config'],
    queryFn: async () => {
      const res = await fetch('/api/config');
      if (!res.ok) throw new Error(`/api/config: HTTP ${res.status}`);
      const data = (await res.json()) as ConfigDto;
      return data.pages ?? [];
    },
    // Visibility changes rarely (an admin action); keep it cheap but not
    // forever-stale so an admin toggle reflects on the next mount.
    staleTime: 60_000,
  });
}

/** qros CLI download URLs for the ? menu; null when the instance hasn't set
 *  TRES_API_QROS_CLI_BASE_URL (the entry stays hidden — never a dead link). */
export function useQrosCliDownloads() {
  return useQuery<QrosCliDownloads | null>({
    queryKey: ['qros-cli-downloads'],
    queryFn: async () => {
      const res = await fetch('/api/config');
      if (!res.ok) throw new Error(`/api/config: HTTP ${res.status}`);
      const data = (await res.json()) as ConfigDto;
      return data.qros_cli ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Whether in-app issue filing is configured server-side. False hides the
 *  "Report an issue…" menu entry — showing a compose form that 503s on
 *  submit is worse than no entry. */
export function useIssuesEnabled() {
  return useQuery<boolean>({
    queryKey: ['issues-enabled'],
    queryFn: async () => {
      const res = await fetch('/api/config');
      if (!res.ok) throw new Error(`/api/config: HTTP ${res.status}`);
      const data = (await res.json()) as ConfigDto;
      return data.issues_enabled ?? false;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Whether the property-graph import exists on this instance. An older
 *  server that does not report it still has the tab. */
export function usePropertyGraphImportEnabled() {
  return useQuery<boolean>({
    queryKey: ['property-graph-import-enabled'],
    queryFn: async () => {
      const res = await fetch('/api/config');
      if (!res.ok) throw new Error(`/api/config: HTTP ${res.status}`);
      const data = (await res.json()) as ConfigDto;
      return data.property_graph_import ?? true;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** The audience for the Welcome page's marketing panes on this instance.
 *  Anything the server does not send is `off` — an instance that has never
 *  heard of the setting must not advertise. */
export function useMarketingPanes() {
  return useQuery<MarketingPanes>({
    queryKey: ['marketing-panes'],
    queryFn: async () => {
      const res = await fetch('/api/config');
      if (!res.ok) throw new Error(`/api/config: HTTP ${res.status}`);
      const data = (await res.json()) as ConfigDto;
      return data.marketing_panes ?? 'off';
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Whether this instance serves visitors who have not signed in.
 *
 *  True keeps `RequireAuth` from bouncing a signed-out visitor to the identity
 *  provider and renders the application read-only instead. Rendering only —
 *  what a guest may actually do is decided server-side by the `Anonymous` role,
 *  so a stale or spoofed `false` here costs a redirect, never access. */
export function useAnonymousRead() {
  return useQuery<boolean>({
    queryKey: ['anonymous-read'],
    queryFn: async () => {
      const res = await fetch('/api/config');
      if (!res.ok) throw new Error(`/api/config: HTTP ${res.status}`);
      const data = (await res.json()) as ConfigDto;
      return data.anonymous_read ?? false;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** The largest upload THIS instance accepts, in bytes — the value the server
 *  enforces with, so what the page prints and what the server refuses cannot
 *  disagree. `null` while loading (no size is printed and no pre-check runs
 *  until the number is known; the server still refuses an oversize body). */
export function useMaxLoadBytes() {
  return useQuery<number | null>({
    queryKey: ['max-load-bytes'],
    queryFn: async () => {
      const res = await fetch('/api/config');
      if (!res.ok) throw new Error(`/api/config: HTTP ${res.status}`);
      const data = (await res.json()) as ConfigDto;
      return data.max_load_bytes ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Per-instance SPA idle-timeout window (minutes); null = use the default. */
export function useSessionIdleMinutes() {
  return useQuery<number | null>({
    queryKey: ['session-idle-minutes'],
    queryFn: async () => {
      const res = await fetch('/api/config');
      if (!res.ok) throw new Error(`/api/config: HTTP ${res.status}`);
      const data = (await res.json()) as ConfigDto;
      return data.session_idle_minutes ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useModulesConfig() {
  return useQuery<PageModule[]>({
    queryKey: ['modules-config'],
    queryFn: async () => {
      const res = await fetch('/api/config');
      if (!res.ok) throw new Error(`/api/config: HTTP ${res.status}`);
      const data = (await res.json()) as ConfigDto;
      return (data.modules ?? []).flatMap((pkg) =>
        pkg.pages.map((p) => ({
          key: p.key,
          module: pkg.id,
          module_label: pkg.label,
          beta: p.beta,
        })),
      );
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** The modules this site is entitled to; `undefined` while loading. An
 *  absent list means every module (a stamp with no entitlement row). */
export function useEntitledModules() {
  return useQuery<ModuleKey[] | null>({
    queryKey: ['entitled-modules'],
    queryFn: async () => {
      const res = await fetch('/api/config');
      if (!res.ok) throw new Error(`/api/config: HTTP ${res.status}`);
      const data = (await res.json()) as ConfigDto;
      return data.entitled_modules ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Whether a module is in the plan: true when the list is absent (no row). */
export function moduleEntitled(list: ModuleKey[] | null | undefined, key: ModuleKey): boolean {
  return list === null || list === undefined || list.includes(key);
}

/** The module assignment for a page key; undefined while loading or for an
 *  unlisted page (renders unbadged — fail-quiet, like page visibility). */
export function pageModule(
  modules: PageModule[] | undefined,
  key: string,
): PageModule | undefined {
  return modules?.find((m) => m.key === key);
}

/** Pages whose every action writes. A guest is refused all of them by the
 *  server, so no page setting can offer one to a guest: the page would be a
 *  set of controls that each end in a refusal. */
const WRITE_ONLY_PAGES: ReadonlySet<string> = new Set(['admin-load']);

/**
 * Whether a page is shown to a user with `role`:
 *
 * - `visible` / `hidden` once that is decided.
 * - `awaiting-role` while the page is scoped to roles and `/api/me` has not
 *   answered yet. Deciding then read an unknown role as a role that matches
 *   nothing, so a guest's direct link to a scoped page landed on Welcome.
 *
 * A page absent from the config defaults to visible (fail-open for UX — the
 * API still enforces RBAC). Always-on pages are always visible. `pages`
 * undefined (still loading) → visible, so nav doesn't flicker empty.
 */
export type PageAccess = 'visible' | 'hidden' | 'awaiting-role';

export function pageAccess(
  pages: EffectivePage[] | undefined,
  key: string,
  // `Anonymous` is deliberately not part of `Role`: that union is the set an
  // admin can ASSIGN, and a guest is not assignable. It is accepted here
  // because a guest still renders a nav.
  role: Role | 'Anonymous' | undefined,
): PageAccess {
  if (role === 'Anonymous' && WRITE_ONLY_PAGES.has(key)) return 'hidden';
  if (!pages) return 'visible';
  const p = pages.find((x) => x.key === key);
  if (!p) return 'visible';
  if (p.always_on) return 'visible';
  if (!p.enabled) return 'hidden';
  // The Owner sees every enabled page — the top seat cannot be locked out of
  // a surface by its own page config. Role scoping ranges over the scopable
  // roles (Admin and below); "Owner" is never stored in allowed_roles and
  // used to match nothing here, redirecting the Owner off every scoped page.
  if (p.allowed_roles.length === 0) return 'visible';
  if (role === undefined) return 'awaiting-role';
  if (role === 'Owner') return 'visible';
  // A GUEST SEES WHAT THE READER SEES (ruled 2026-08-13). Anonymous is the
  // reader tier without an account: same pages, no editing. Treating it as a
  // role that matched nothing meant a signed-out visitor saw only the pages
  // nobody had scoped — two of them — which is not a product, and it put page
  // display outside the admin's control precisely where it was needed most.
  // The admin decides what a guest sees by scoping pages to `User`.
  const effective = role === 'Anonymous' ? 'User' : role;
  return p.allowed_roles.includes(effective) ? 'visible' : 'hidden';
}

/** Whether a menu or tab offers the page. A page still waiting on the role is
 *  offered: hiding it would make the menu shrink and regrow on every load, and
 *  the route's `PageGuard` makes the decision once the role is known. */
export function isPageVisible(
  pages: EffectivePage[] | undefined,
  key: string,
  role: Role | 'Anonymous' | undefined,
): boolean {
  return pageAccess(pages, key, role) !== 'hidden';
}

/**
 * Whether a page is usable on a phone-sized viewport, per the server page
 * registry. Same fail-open semantics as `isPageVisible`: loading or unknown
 * → true, so the nav doesn't flicker. Callers combine this with
 * `useIsMobile()` — on a full-size viewport the flag is irrelevant.
 */
export function isPageMobile(pages: EffectivePage[] | undefined, key: string): boolean {
  if (!pages) return true;
  const p = pages.find((x) => x.key === key);
  return p ? p.mobile : true;
}

/** The site's query time budget, for the running notice; null until known. */
export function useQueryTimeoutSecs() {
  return useQuery<number | null>({
    queryKey: ['query-timeout-secs'],
    queryFn: async () => {
      const res = await fetch('/api/config');
      if (!res.ok) throw new Error(`/api/config: HTTP ${res.status}`);
      const data = (await res.json()) as ConfigDto;
      return data.query_timeout_secs ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });
}
